import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN ?? '';
const sentryEnabled = !!DSN && import.meta.env.VITE_SENTRY_ENABLED;

export function initSentry() {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: import.meta.env.VITE_SENTRY_ENABLED,
  });
}

export { Sentry };

// ---------------------------------------------------------------------------
// Logger — wraps Sentry when available, falls back to console
// ---------------------------------------------------------------------------

export const logger = {
  /** Report an error to Sentry (or console in dev). */
  error(error: unknown, extra?: Record<string, unknown>) {
    if (sentryEnabled) {
      Sentry.captureException(error, extra ? { extra } : undefined);
    } else {
      console.error('[error]', error, extra ?? '');
    }
  },

  /** Log a warning message. */
  warn(message: string, extra?: Record<string, unknown>) {
    if (sentryEnabled) {
      Sentry.captureMessage(message, { level: 'warning', extra });
    } else {
      console.warn('[warn]', message, extra ?? '');
    }
  },

  /** Log an informational message (breadcrumb in Sentry, console in dev). */
  info(message: string, data?: Record<string, unknown>) {
    if (sentryEnabled) {
      Sentry.addBreadcrumb({ message, data, level: 'info' });
    } else {
      console.log('[info]', message, data ?? '');
    }
  },

  /** Set the current user context for error reports. */
  setUser(user: { id: string; email?: string } | null) {
    if (sentryEnabled) {
      Sentry.setUser(user);
    }
  },
};
